import React, { createContext, useState, useContext, useEffect } from 'react';
import { CHAT_STORAGE_KEYS } from './chatStorage';

const ChatContext = createContext();

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  // Initialize state from localStorage or defaults
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('chatMessages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Convert timestamp strings back to Date objects
        return parsed.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch (e) {
        console.error('Failed to parse saved messages:', e);
      }
    }
    // textKey: translated when rendered, so the greeting follows the UI language (#382)
    return [
      {
        id: 1,
        textKey: 'aiChatbot.greeting',
        sender: 'bot',
        timestamp: new Date()
      }
    ];
  });

  const [artifacts, setArtifacts] = useState(() => {
    const saved = localStorage.getItem('chatArtifacts');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Convert timestamp strings back to Date objects
        return parsed.map(artifact => ({
          ...artifact,
          timestamp: new Date(artifact.timestamp)
        }));
      } catch (e) {
        console.error('Failed to parse saved artifacts:', e);
      }
    }
    return [];
  });

  const [selectedArtifact, setSelectedArtifact] = useState(() => {
    const saved = localStorage.getItem('selectedArtifact');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse selected artifact:', e);
      }
    }
    return null;
  });

  const [showArtifacts, setShowArtifacts] = useState(() => {
    const saved = localStorage.getItem('showArtifacts');
    return saved === 'true';
  });

  const [conversationHistory, setConversationHistory] = useState(() => {
    const saved = localStorage.getItem('conversationHistory');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse conversation history:', e);
      }
    }
    return [];
  });

  // Server-side conversation being continued (null = next message starts a new one)
  const [conversationId, setConversationId] = useState(() => {
    const saved = localStorage.getItem('aiConversationId');
    return saved ? Number(saved) : null;
  });

  // Scroll position state - not persisted to localStorage
  const [scrollPosition, setScrollPosition] = useState(0);

  // Auto-save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('chatMessages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('chatArtifacts', JSON.stringify(artifacts));
  }, [artifacts]);

  useEffect(() => {
    if (selectedArtifact) {
      localStorage.setItem('selectedArtifact', JSON.stringify(selectedArtifact));
    } else {
      localStorage.removeItem('selectedArtifact');
    }
  }, [selectedArtifact]);

  useEffect(() => {
    localStorage.setItem('showArtifacts', showArtifacts.toString());
  }, [showArtifacts]);

  useEffect(() => {
    localStorage.setItem('conversationHistory', JSON.stringify(conversationHistory));
  }, [conversationHistory]);

  useEffect(() => {
    if (conversationId) {
      localStorage.setItem('aiConversationId', String(conversationId));
    } else {
      localStorage.removeItem('aiConversationId');
    }
  }, [conversationId]);

  // Clear all chat data
  const clearChatData = () => {
    setMessages([
      {
        id: Date.now(),
        textKey: 'aiChatbot.greeting',
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
    setArtifacts([]);
    setSelectedArtifact(null);
    setShowArtifacts(false);
    setConversationHistory([]);
    setConversationId(null);

    // Clear from localStorage (the owner stays: it is still this user's browser chat)
    CHAT_STORAGE_KEYS.forEach(k => localStorage.removeItem(k));
  };

  const value = {
    messages,
    setMessages,
    artifacts,
    setArtifacts,
    selectedArtifact,
    setSelectedArtifact,
    showArtifacts,
    setShowArtifacts,
    conversationHistory,
    setConversationHistory,
    conversationId,
    setConversationId,
    scrollPosition,
    setScrollPosition,
    clearChatData
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;